/**
 * Which slice of the day the hour grid should render.
 *
 * The grid is fixed at 0-24 today, so nine tenths of the scroll is empty
 * night and a rep hunting a 3pm meeting scrolls past fifteen dead rows to
 * find it. The org already tells us when it works — `businessHours` under
 * `settingsOverrides` — so that is the slice we draw.
 *
 * One rule governs everything here: THE WINDOW MAY NEVER HIDE AN EVENT.
 * `getEventGeometry` returns null for anything outside the window, so a
 * 07:00 meeting against 09:00-18:00 hours would not render at all — silent
 * data loss dressed up as a tidier view. `resolveDayWindow` therefore widens
 * the window around whatever the day actually holds; business hours set the
 * floor, never the ceiling.
 *
 * Pure: no React, no pixels. Pixel geometry is calendarHourGrid.ts's job.
 */
import type { CalendarEvent } from '../../../types/calendar';
import type { BusinessDayKey, BusinessHours } from '../../settings/utils/businessHours';
import { dayOfWeek, type LocalDate, localDateKey } from './calendarDate';
import {
  DEFAULT_DAY_WINDOW,
  localMinutesSinceMidnight,
  type TimeWindow,
} from './calendarDayGeometry';
import { eventTouchesLocalDay } from './calendarEventBucketing';

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

// dayOfWeek is 0=Sunday (JS convention); the stored config keys start at Monday.
const DAY_KEYS_BY_WEEKDAY: readonly BusinessDayKey[] = [
  'sun',
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
];

const HH_MM = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * "09:30" -> 570. Null for anything else.
 *
 * `settingsOverrides` is a free-form JSON bag on the org row, so nothing on
 * the way in guarantees this column holds a time. A lenient parse here would
 * turn a typo into a silently wrong grid.
 */
export function parseTimeOfDay(value: string): number | null {
  if (typeof value !== 'string') return null;

  const match = HH_MM.exec(value);
  if (!match) return null;

  return Number(match[1]) * MINUTES_PER_HOUR + Number(match[2]);
}

export function businessDayKeyFor(date: LocalDate): BusinessDayKey {
  return DAY_KEYS_BY_WEEKDAY[dayOfWeek(date)];
}

/**
 * The org's configured hours across `days`, as one window — the union, not a
 * per-day slice, because week view renders seven columns against a single
 * shared hour gutter and they must agree on what a row means.
 *
 * Null means "no usable configuration": not enabled, nothing stored, or
 * nothing that parses. The caller reads that as the full day, because an org
 * that has not opted in is asking to see everything, not to see 9 to 5.
 */
export function getBusinessWindow(
  hours: BusinessHours | null | undefined,
  days: readonly BusinessDayKey[],
): TimeWindow | null {
  if (!hours?.enabled || days.length === 0) return null;

  const requested = new Set(days);
  let startMinutes = Number.POSITIVE_INFINITY;
  let endMinutes = Number.NEGATIVE_INFINITY;

  for (const window of hours.windows ?? []) {
    if (!requested.has(window.day)) continue;

    const start = parseTimeOfDay(window.start);
    const end = parseTimeOfDay(window.end);
    // An end at or before its start describes no time at all, and would make
    // getEventGeometry divide by a zero-or-negative span.
    if (start === null || end === null || end <= start) continue;

    startMinutes = Math.min(startMinutes, start);
    endMinutes = Math.max(endMinutes, end);
  }

  if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes)) return null;

  return { startMinutes, endMinutes };
}

function floorToHour(minutes: number): number {
  return Math.floor(minutes / MINUTES_PER_HOUR) * MINUTES_PER_HOUR;
}

function ceilToHour(minutes: number): number {
  return Math.ceil(minutes / MINUTES_PER_HOUR) * MINUTES_PER_HOUR;
}

/**
 * The window the grid actually renders: the business window (or the whole day
 * when there is none), widened to contain every span given, snapped out to
 * whole hours because the grid draws hour ROWS, and clamped to the real day.
 *
 * `spans` are minutes since local midnight and may fall outside 0-1440 — an
 * event crossing midnight reports exactly that, and `localMinutesSinceMidnight`
 * is deliberately unclamped.
 */
export function resolveDayWindow(
  businessWindow: TimeWindow | null,
  spans: readonly TimeWindow[],
): TimeWindow {
  const base = businessWindow ?? DEFAULT_DAY_WINDOW;

  let startMinutes = base.startMinutes;
  let endMinutes = base.endMinutes;

  for (const span of spans) {
    startMinutes = Math.min(startMinutes, span.startMinutes);
    endMinutes = Math.max(endMinutes, span.endMinutes);
  }

  startMinutes = Math.max(0, floorToHour(startMinutes));
  endMinutes = Math.min(MINUTES_PER_DAY, ceilToHour(endMinutes));

  // A degenerate window would divide by zero downstream; one hour is the
  // smallest thing the row-based grid can express.
  if (endMinutes - startMinutes < MINUTES_PER_HOUR) {
    endMinutes = Math.min(MINUTES_PER_DAY, startMinutes + MINUTES_PER_HOUR);
    startMinutes = Math.max(0, endMinutes - MINUTES_PER_HOUR);
  }

  return { startMinutes, endMinutes };
}

interface VisibleDayWindowOptions {
  businessHours: BusinessHours | null | undefined;
  /** The days on screen: one for Day view, seven for Week. */
  days: readonly LocalDate[];
  events: readonly CalendarEvent[];
  timeZone: string;
}

/**
 * The window the Day and Week grids render: the org's hours for the weekdays
 * on screen, widened around every timed event those days actually hold.
 *
 * All-day events are excluded on purpose. They are rendered in their own strip
 * above the grid, never on it, and their 00:00-24:00 span would force the
 * window back open to the full day every time one appeared.
 */
export function getVisibleDayWindow({
  businessHours,
  days,
  events,
  timeZone,
}: VisibleDayWindowOptions): TimeWindow {
  const businessWindow = getBusinessWindow(businessHours, days.map(businessDayKeyFor));

  const spans: TimeWindow[] = [];
  for (const date of days) {
    const dateKey = localDateKey(date);
    for (const event of events) {
      if (event.allDay) continue;
      if (!eventTouchesLocalDay(event, dateKey, timeZone)) continue;

      spans.push({
        startMinutes: localMinutesSinceMidnight(event.startsAt, date, timeZone),
        endMinutes: localMinutesSinceMidnight(event.endsAt, date, timeZone),
      });
    }
  }

  return resolveDayWindow(businessWindow, spans);
}
